import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { apiGetJobs } from '@/features/jobs/api/api-get-jobs'
import { JobFiltersPanel } from '@/features/jobs/components/job-filters-panel'
import { JobStatusLabel } from '@/features/jobs/components/job-status-label'
import { TownCountyCell } from '@/features/jobs/components/town-county-cell'
import { formatDateRaised } from '@/features/jobs/lib/format-date-raised'
import { formatJobSeverity } from '@/features/jobs/lib/format-job-severity'
import { formatJobType } from '@/features/jobs/lib/format-job-type'
import {
  DEFAULT_JOB_LIST_SORT,
  nextJobListSort,
  type JobListSort,
} from '@/features/jobs/lib/job-list-sort'
import {
  emptyJobFilters,
  type JobFilters,
} from '@/features/jobs/types/job-filters'
import type { Job } from '@/features/jobs/types/job'
import { emptyPagination } from '@/lib/api/empty-pagination'
import {
  Button,
  ListTable,
  LoadingSpinner,
  PaginationLinks,
  type ListTableColumn,
} from '@/shared/components'
import { useApiResource } from '@/shared/hooks/use-api-resource'
import FilterIcon from '@/assets/icons/filter.svg'
import { Plus } from 'lucide-react';

export const JobsPage = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<JobFilters>(emptyJobFilters())
  const [sort, setSort] = useState<JobListSort>(DEFAULT_JOB_LIST_SORT)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  const loadJobs = useCallback(
    () => apiGetJobs(filters, page, sort),
    [filters, page, sort]
  )
  const { data, error, isLoading } = useApiResource(loadJobs, 'Failed to load jobs')
  const jobs = data?.data ?? []
  const pagination = data?.pagination ?? emptyPagination()

  const handleApplyFilters = (nextFilters: JobFilters) => {
    setPage(1)
    setFilters(nextFilters)
  }

  const handleSort = (columnId: string) => {
    setPage(1)
    setSort((current) => nextJobListSort(current, columnId))
  }

  const columns = useMemo<ListTableColumn<Job>[]>(
    () => [
      {
        id: 'date_raised',
        header: 'Date Raised',
        cell: (job) => formatDateRaised(job.date_raised),
      },
      {
        id: 'assigned_partner_name',
        header: 'SiteWorks Partner',
        cell: (job) => job.assigned_partner_name,
      },
      {
        id: 'job_type',
        header: 'Job Type',
        cell: (job) => formatJobType(job.job_type),
      },
      {
        id: 'location_id',
        header: 'Location ID',
        cell: (job) => <span className="font-bold">{job.location.location_id}</span>,
      },
      {
        id: 'road_name',
        header: 'Road Name',
        cell: (job) => job.location.road_name ?? '',
      },
      {
        id: 'town_county',
        header: 'City/Town \n County',
        cell: (job) => <TownCountyCell location={job.location} />,
      },
      {
        id: 'asset_location',
        header: 'Asset Location',
        cell: (job) => job.location.asset_location ?? '',
      },
      {
        id: 'severity',
        header: 'Severity',
        cell: (job) => formatJobSeverity(job.severity),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (job) => <JobStatusLabel status={job.status} />,
      },
    ],
    []
  )

  const tableContent = (() => {
    if (isLoading) {
      return <LoadingSpinner position="center" label="Loading jobs" />
    }

    if (error) {
      return (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )
    }

    return (
      <>
        <ListTable
          columns={columns}
          data={jobs}
          getRowKey={(job) => job.id}
          emptyMessage="No jobs found."
          onRowClick={(job) => navigate(`/jobs/${job.id}`)}
          sort={{ columnId: sort.column, direction: sort.direction }}
          onSort={handleSort}
        />

        <PaginationLinks
          pagination={pagination}
          onPageChange={setPage}
          className="pt-4"
        />
      </>
    )
  })()

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <Button
            type="button"
            icon={FilterIcon}
            variant="secondary-light"
            className="self-start"
            onClick={() => setIsFiltersOpen(true)}
          >
            Filter
          </Button>

          <Button asChild type="button" variant="action">
            <Link to="/jobs/new">
              <Button.Icon>
                <Plus />
              </Button.Icon>
              Raise new job
            </Link>
          </Button>
        </div>

        {tableContent}
      </div>

      <JobFiltersPanel
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        filters={filters}
        onApply={handleApplyFilters}
      />
    </>
  )
}
